import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, Loader2 } from "lucide-react";

export interface GlassModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl"
    /** Nearly the whole screen (96vw × 96vh): editors with a side preview. */
    | "screen";
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  /**
   * Fermeture au clavier. Suit `closeOnBackdropClick` par défaut : une modale
   * qu'on a protégée du clic à côté est une modale qu'on ne veut pas voir
   * disparaître par mégarde, et Échap est la même mégarde au clavier. Séparé
   * pour que le prochain appelant puisse dissocier les deux sans détourner un
   * prop qui parle de la souris.
   */
  closeOnEscape?: boolean;
  headerContent?: React.ReactNode;
  panelClassName?: string;
}

/** Ce qui peut recevoir le focus au Tab, sans les nœuds masqués. */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable='false'])",
].join(",");

/** `checkVisibility()` plutôt qu'`offsetParent` : ce dernier vaut `null` sur un
 *  élément `position: fixed` visible, et le panneau en contient (en-tête et pied
 *  collants). L'API est absente de jsdom, qui ne fait aucune mise en page — sans
 *  repli, tout y paraîtrait masqué et le piège à focus se viderait sous les
 *  tests. Un contrôle réellement masqué qu'on laisserait alors passer ne casse
 *  rien de plus qu'un `focus()` sans effet. */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((node) =>
    typeof node.checkVisibility === "function" ? node.checkVisibility() : true,
  );
}

/**
 * GlassModal - Shared modal component with glass morphism design
 *
 * This component implements the standard glass design pattern used across the app:
 * - Dark glass background (bg-neutral-900/95)
 * - Backdrop blur effect
 * - Consistent spacing and borders
 * - Proper white text on dark background for readability
 *
 * Usage:
 * ```tsx
 * <GlassModal
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   title="Modal Title"
 *   maxWidth="3xl"
 * >
 *   <div className="space-y-4">
 *     ... modal content ...
 *   </div>
 * </GlassModal>
 * ```
 *
 * Clavier et lecteurs d'écran : le panneau est un `role="dialog"`
 * `aria-modal`, étiqueté par son titre. Il prend le focus à l'ouverture, le
 * retient (Tab boucle dans le panneau), le rend à son ouvrant à la fermeture,
 * et Échap ferme. Sans cela, ouvrir la modale au clavier laissait le focus
 * derrière la surcouche : on tabulait dans une page qu'on ne voyait plus.
 */
export function GlassModal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth = "3xl",
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = closeOnBackdropClick,
  headerContent,
  panelClassName = "",
}: GlassModalProps) {
  const { t } = useTranslation("common");
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const openerRef = React.useRef<HTMLElement | null>(null);
  const mouseDownTarget = React.useRef<EventTarget | null>(null);
  const titleId = React.useId();

  // Tous les hooks AVANT le retour anticipé : les appeler après ferait varier
  // leur nombre d'un rendu à l'autre dès que `isOpen` bascule, ce que React
  // refuse.
  React.useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel) {
      const first = focusableWithin(panel)[0];
      (first ?? panel).focus();
    }
    return () => {
      const opener = openerRef.current;
      // L'ouvrant peut avoir disparu avec l'action — le bouton d'une ligne que
      // la modale vient de supprimer. Navigateurs et jsdom ignorent déjà un
      // `focus()` sur un nœud détaché, donc aucun test ne peut distinguer ce
      // garde-fou : il est là pour ne pas dépendre de ce détail.
      if (opener && opener.isConnected) opener.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    screen: "max-w-[96vw]",
  };
  const maxHeightClass = maxWidth === "screen" ? "max-h-[96vh]" : "max-h-[90vh]";

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseDownTarget.current = e.target;
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      closeOnBackdropClick &&
      e.target === e.currentTarget &&
      mouseDownTarget.current === e.currentTarget
    ) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      if (!closeOnEscape) return;
      // Déjà traité par un enfant (une liste ouverte qui ferme son menu) : la
      // modale reste ouverte, sinon la saisie en cours est perdue.
      if (e.defaultPrevented) return;
      // Une modale imbriquée se ferme seule : les portails propagent les
      // événements React le long de l'arbre React, donc sans cet arrêt un Échap
      // dans la modale de confirmation fermerait aussi le tiroir qui la porte.
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = focusableWithin(panel);
    if (items.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      // Le panneau lui-même compte comme « avant le premier » : c'est lui qui
      // porte le focus quand la modale n'a pas encore été tabulée.
      if (active === first || active === panel || !panel.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      onMouseDown={handleMouseDown}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`bg-neutral-900/95 backdrop-blur-lg rounded-lg ${maxWidthClasses[maxWidth]} w-full ${maxHeightClass} overflow-y-auto scrollbar-hidden shadow-overlay border border-white/10 focus:outline-none ${panelClassName}`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-neutral-900/95 backdrop-blur-lg border-b border-white/10 px-6 py-4 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 id={titleId} className="text-xl font-semibold text-white">
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm text-white/60 mt-1">{subtitle}</p>
              )}
              {headerContent && <div className="mt-2">{headerContent}</div>}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t("actions.close")}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white flex-shrink-0"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="sticky bottom-0 bg-neutral-900/40 backdrop-blur-lg border-t border-white/10 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * GlassModalFooter - Standard footer component for GlassModal
 *
 * Provides consistent button layout (cancel left, action right)
 */
export interface GlassModalFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  cancelLabel?: string;
  confirmLabel?: React.ReactNode;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  confirmVariant?:
    | "primary"
    | "neutral"
    | "danger"
    | "success"
    | "warning"
    | "ghost";
  additionalContent?: React.ReactNode;
}

export function GlassModalFooter({
  onCancel,
  onConfirm,
  cancelLabel,
  confirmLabel,
  confirmDisabled = false,
  confirmLoading = false,
  confirmVariant = "primary",
  additionalContent,
}: GlassModalFooterProps) {
  const { t } = useTranslation("common");
  const resolvedCancelLabel = cancelLabel ?? t("actions.cancel");
  const resolvedConfirmLabel = confirmLabel ?? t("actions.confirm");
  return (
    <div className="flex items-center justify-between">
      <GlassButton variant="ghost" onClick={onCancel}>
        {resolvedCancelLabel}
      </GlassButton>
      <div className="flex items-center gap-3">
        {additionalContent}
        <GlassButton
          variant={confirmVariant}
          onClick={onConfirm}
          disabled={confirmDisabled || confirmLoading}
          loading={confirmLoading}
        >
          {resolvedConfirmLabel}
        </GlassButton>
      </div>
    </div>
  );
}

/**
 * GlassButton - Standard button component with glass design
 */
export interface GlassButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "neutral" | "danger" | "success" | "warning" | "ghost";
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

export function GlassButton({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled = false,
  loading = false,
  className = "",
}: GlassButtonProps) {
  const { t } = useTranslation("common");
  const variantClasses = {
    primary:
      "bg-primary-500/20 border-primary-500/30 text-primary-100 hover:bg-primary-500/30",
    neutral: "bg-white/5 border-white/10 text-white hover:bg-white/10",
    danger:
      "bg-rose-500/20 border-rose-500/30 text-rose-100 hover:bg-rose-500/30",
    success:
      "bg-emerald-500/20 border-emerald-500/30 text-emerald-100 hover:bg-emerald-500/30",
    warning:
      "bg-yellow-500/20 border-yellow-500/30 text-yellow-100 hover:bg-yellow-500/30",
    ghost: "bg-white/5 border-white/10 text-white hover:bg-white/10",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`px-4 py-2 border rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 ${variantClasses[variant]} ${className}`}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {loading ? t("loading.processing") : children}
    </button>
  );
}

/**
 * GlassInput - Standard input field with glass design
 */
export interface GlassInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  required?: boolean;
}

export function GlassInput({
  label,
  error,
  icon,
  required,
  className = "",
  ...props
}: GlassInputProps) {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-white/80 mb-1">
          {label}
          {required && <span className="text-rose-400 ms-1">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none text-white/40">
            {icon}
          </div>
        )}
        <input
          {...props}
          className={`w-full px-3 ${icon ? "ps-10" : ""} py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-white/40 focus:ring-primary-500 focus:border-primary-500 focus:outline-none ${error ? "border-rose-500" : ""} ${className}`}
        />
      </div>
      {error && <p className="mt-1 text-xs text-rose-300">{error}</p>}
    </div>
  );
}

/**
 * GlassModalSection - Reusable section component for organizing modal content
 *
 * Provides consistent spacing and optional title for sections within a modal
 */
export interface GlassModalSectionProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function GlassModalSection({
  title,
  children,
  className = "",
}: GlassModalSectionProps) {
  return (
    <div className={`space-y-4 ${className}`}>
      {title && (
        <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wide">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
